<template>
  <fieldset class="annotation-style-editor">
    <legend>Detalles del punto</legend>

    <p class="annotation-style-help">
      {{ isReadonly
        ? 'Apariencia del punto en el mapa.'
        : 'Personaliza cómo se verá esta anotación en el mapa.' }}
    </p>

    <div class="annotation-style-content">
      <div class="annotation-style-preview" aria-label="Vista previa del punto">
        <span
          v-if="selectedIcon"
          class="annotation-icon-preview"
          :style="{
            backgroundColor: normalizedValue.color,
            maskImage: 'url(' + selectedIcon.src + ')',
            WebkitMaskImage: 'url(' + selectedIcon.src + ')',
            transform: 'scale(' + previewScale + ')'
          }"
        ></span>

        <svg
          v-else
          viewBox="0 0 48 48"
          role="img"
          aria-hidden="true"
          :style="{ transform: 'scale(' + previewScale + ')' }"
        >
          <circle
            v-if="normalizedValue.symbol === 'circle'"
            cx="24"
            cy="24"
            r="13"
            :fill="normalizedValue.color"
          />
          <rect
            v-else-if="normalizedValue.symbol === 'square'"
            x="11"
            y="11"
            width="26"
            height="26"
            rx="2"
            :fill="normalizedValue.color"
          />
          <polygon
            v-else-if="normalizedValue.symbol === 'diamond'"
            points="24,8 40,24 24,40 8,24"
            :fill="normalizedValue.color"
          />
          <polygon
            v-else-if="normalizedValue.symbol === 'triangle'"
            points="24,8 41,38 7,38"
            :fill="normalizedValue.color"
          />
          <polygon
            v-else
            points="24,5 29.6,16.3 42,18.1 33,26.9 35.1,39.3 24,33.5 12.9,39.3 15,26.9 6,18.1 18.4,16.3"
            :fill="normalizedValue.color"
          />
        </svg>
      </div>

      <div v-if="isReadonly" class="annotation-style-summary">
        <span>{{ symbolLabel }}</span>
        <span>{{ normalizedValue.size }} px</span>
        <span class="annotation-style-color-value">
          <i :style="{ backgroundColor: normalizedValue.color }"></i>
          {{ normalizedValue.color.toUpperCase() }}
        </span>
        <span>
          {{ normalizedValue.show_label ? 'Etiqueta visible' : 'Etiqueta oculta' }}
        </span>
      </div>

      <div v-else class="annotation-style-controls">
        <div class="annotation-form-group">
          <label :for="fieldId('symbol')">Símbolo</label>
          <select
            :id="fieldId('symbol')"
            :value="normalizedValue.symbol"
            @change="updateField('symbol', $event.target.value)"
          >
            <optgroup label="Figuras geométricas">
              <option
                v-for="option in shapeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </optgroup>

            <optgroup label="Iconos">
              <option
                v-for="option in iconOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </optgroup>
          </select>
        </div>

        <div class="annotation-form-group">
          <label :for="fieldId('color')">Color</label>
          <div class="annotation-color-control">
            <input
              :id="fieldId('color')"
              type="color"
              :value="normalizedValue.color"
              @input="updateField('color', $event.target.value)"
            >
            <span>{{ normalizedValue.color.toUpperCase() }}</span>
          </div>
        </div>

        <div class="annotation-form-group annotation-size-control">
          <label :for="fieldId('size')">
            Tamaño
            <output :for="fieldId('size')">{{ normalizedValue.size }} px</output>
          </label>
          <input
            :id="fieldId('size')"
            type="range"
            min="6"
            max="24"
            step="1"
            :value="normalizedValue.size"
            @input="updateField('size', Number($event.target.value))"
          >
        </div>

        <label
          class="annotation-label-control"
          :for="fieldId('show-label')"
        >
          <input
            :id="fieldId('show-label')"
            type="checkbox"
            :checked="normalizedValue.show_label"
            @change="updateField('show_label', $event.target.checked)"
          >
          <span>
            <strong>Mostrar mensaje como etiqueta</strong>
            <small>El texto se visualizará junto al punto en el mapa.</small>
          </span>
        </label>
      </div>
    </div>
  </fieldset>
</template>

<script>
import {
  ANNOTATION_SYMBOLS,
  normalizeAnnotationStyle
} from './annotationStyles.js'
import { getAnnotationIcon } from './annotationIcons.js'

let editorSequence = 0

export default {
  name: 'AnnotationStyleEditor',
  props: {
    value: {
      type: Object,
      default: function () {
        return {}
      }
    },
    isReadonly: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    editorSequence += 1
    return {
      editorId: 'annotation-style-' + editorSequence,
      symbolOptions: ANNOTATION_SYMBOLS
    }
  },
  computed: {
    normalizedValue: function () {
      return normalizeAnnotationStyle(this.value)
    },
    symbolLabel: function () {
      const selected = this.symbolOptions.find(option => option.value === this.normalizedValue.symbol)
      return selected ? selected.label : 'Círculo'
    },
    selectedIcon: function () {
      return getAnnotationIcon(this.normalizedValue.symbol)
    },
    shapeOptions: function () {
      return this.symbolOptions.filter(option => option.type === 'shape')
    },
    iconOptions: function () {
      return this.symbolOptions.filter(option => option.type === 'icon')
    },
    previewScale: function () {
      return 0.55 + ((this.normalizedValue.size - 6) / 40)
    }
  },
  methods: {
    fieldId: function (field) {
      return this.editorId + '-' + field
    },
    updateField: function (field, value) {
      const updatedStyle = Object.assign({}, this.normalizedValue)
      updatedStyle[field] = value
      this.$emit('input', normalizeAnnotationStyle(updatedStyle))
    }
  }
}
</script>

<style scoped lang="scss">
.annotation-style-editor {
  min-width: 0;
  margin: 0;
  padding: 13px;

  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 5px;

  legend {
    padding: 0 5px;
    color: var(--color-navy);
    font-size: 0.84rem;
    font-weight: 700;
  }
}

.annotation-style-help {
  margin: 0 0 12px;
  color: var(--color-ghost);
  font-size: 0.76rem;
  line-height: 1.4;
}

.annotation-style-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

.annotation-style-preview {
  display: grid;
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  place-items: center;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 5px;

  svg {
    width: 46px;
    height: 46px;
    filter: drop-shadow(0 0 1px rgba(14, 42, 64, 0.7));
    transition: transform 0.15s ease;
  }

  svg > * {
    stroke: var(--color-white);
    stroke-width: 2;
    stroke-linejoin: round;
  }
}

.annotation-icon-preview {
  display: block;
  width: 46px;
  height: 46px;

  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;

  filter: drop-shadow(0 0 1px rgba(14, 42, 64, 0.7));
  transition: transform 0.15s ease;
}

.annotation-style-controls {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px 12px;
}

.annotation-color-control {
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="color"] {
    width: 42px;
    height: 35px;
    padding: 3px;
    cursor: pointer;
  }

  span {
    color: var(--color-graphite);
    font-size: 0.76rem;
    font-variant-numeric: tabular-nums;
  }
}

.annotation-size-control {
  grid-column: 1 / -1;

  label {
    display: flex;
    justify-content: space-between;
  }

  output {
    color: var(--color-ghost);
    font-size: 0.76rem;
    font-weight: 600;
  }

  input[type="range"] {
    padding: 0;
    accent-color: var(--color-orange);
  }
}

.annotation-label-control {
  display: flex;
  grid-column: 1 / -1;
  align-items: flex-start;
  gap: 9px;
  margin: 2px 0 0;
  padding-top: 10px;

  border-top: 1px solid var(--color-line);
  cursor: pointer;

  input {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    accent-color: var(--color-orange);
  }

  span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: var(--color-navy);
    font-size: 0.78rem;
  }

  small {
    color: var(--color-ghost);
    font-size: 0.72rem;
    font-weight: 400;
    line-height: 1.35;
  }
}

.annotation-style-summary {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 7px 14px;

  color: var(--color-graphite);
  font-size: 0.8rem;
}

.annotation-style-color-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  i {
    width: 12px;
    height: 12px;
    border: 1px solid var(--color-line);
    border-radius: 50%;
  }
}

@media (max-width: 480px) {
  .annotation-style-content {
    align-items: flex-start;
  }

  .annotation-style-controls {
    grid-template-columns: 1fr;
  }

  .annotation-size-control {
    grid-column: auto;
  }

  .annotation-label-control {
    grid-column: auto;
  }
}
</style>
